/**
 * The whiteboard and the marks on a shared screen, as arithmetic.
 *
 * Gordon, 22 October: "a whiteboard component to the calls where users can
 * interact with each other", and, over a shared presentation, "any user can
 * highlight or mark certain areas of the shared screen with everybody seeing
 * those marks without those marks editing whatever is being shown."
 *
 * THIS FILE IMPORTS NOTHING, for the reason lib/sessionRoles.js imports
 * nothing: everything in it is a function of its arguments, so
 * e2e/session-draw.mjs loads it straight into Node and checks the parts whose
 * failures are silent. A mark that lands an inch to the left on somebody
 * else's screen throws nothing; neither does a message from a peer that is
 * three megabytes of nonsense. Both are caught here or nowhere.
 *
 * COORDINATES ARE NORMALISED, 0..1 across the surface - the board's 16:9
 * paper, or the shared screen's own picture - never pixels. Everybody's
 * window is a different size, and a shared screen is letterboxed differently
 * in each of them (object-fit: contain), so a pixel means nothing to anybody
 * else. A fraction of the picture means the same spot on every screen.
 *
 * WIDTHS ARE IN THOUSANDTHS OF THE SURFACE'S WIDTH, for the same reason: a
 * pen 3 wide on a 1000-pixel board is 3 pixels there and 1.5 on a phone, and
 * so it covers the same part of the drawing for both.
 */

/** The data channel's name, the same at both ends of every pair. */
export const DRAW_LABEL = 'jarvis-draw';

/**
 * The largest message either end will send or read. SCTP will carry more,
 * but nothing a person draws needs it: a stroke goes out in batches every
 * 30 ms, and a sync for a late joiner is cut into pieces this size.
 */
export const MAX_MESSAGE = 60 * 1024;
/** Points in one stroke; a scribble longer than this stops growing. */
export const MAX_POINTS = 4000;
/** Points in one message. */
export const MAX_BATCH = 800;
/** Strokes on one surface. A board with more is a board nobody can read. */
export const MAX_STROKES = 3000;
/** Characters in a typed label. Drawn with fillText - never as HTML. */
export const MAX_TEXT = 120;

/** The board's paper. Fixed, so a phone and a wide monitor line up. */
export const BOARD_ASPECT = 16 / 9;

/**
 * The colours, by NAME rather than by value. A peer sends "red"; each page
 * turns it into its own --color-swatch-red. That keeps every colour a token,
 * and it means nothing a peer sends is ever handed to the canvas as a style
 * string. 'ink' is the page's own text colour - dark on a light board, light
 * on a dark one - so it is offered on the board only: on a shared screen the
 * picture is the same for everybody, and a mark should be too.
 */
export const COLORS = {
  ink: '--color-text-primary',
  red: '--color-swatch-red',
  orange: '--color-swatch-orange',
  yellow: '--color-swatch-yellow',
  green: '--color-swatch-green',
  blue: '--color-swatch-blue',
  purple: '--color-swatch-purple',
  pink: '--color-swatch-pink'
};
export const BOARD_COLORS = ['ink', 'red', 'orange', 'green', 'blue', 'purple'];
export const MARK_COLORS = ['red', 'orange', 'yellow', 'green', 'blue', 'purple'];

/** What a stroke can be. The eraser and the laser are acts, not strokes. */
export const TOOLS = ['pen', 'highlighter', 'line', 'arrow', 'rect', 'ellipse', 'text'];
/** Tools whose points grow as the hand moves; the rest are two corners. */
export const FREEHAND = new Set(['pen', 'highlighter']);

/** Widths, in thousandths of the surface's width. */
export const WIDTHS = { pen: 4, highlighter: 18, line: 4, arrow: 4, rect: 4, ellipse: 4, text: 30 };

/**
 * The thinnest a line is ever drawn, in pixels, whatever its width says. A
 * pen four thousandths wide is under a pixel and a half on a small tile,
 * and a mark nobody can see is not a mark; so a thin line is held at two
 * pixels (a highlighter at six), and only lines wider than that scale.
 */
export const MIN_PX = { highlighter: 6, other: 2 };

const ID_RE = /^[\w-]{1,80}$/;
const TARGET_RE = /^(board|share:[\w-]{1,64})$/;

/* ---------------------------------------------------------------- geometry */

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
/** Four decimals: a ten-thousandth of a 4K screen is under half a pixel. */
const round4 = (v) => Math.round(v * 10000) / 10000;

/**
 * Where a picture actually sits inside its box.
 *
 * object-fit: contain scales the picture to fit and centres it, leaving bars
 * on two sides. The bars are not the picture, and a mark normalised against
 * the element rather than the picture is off by the width of a bar - which
 * is different on every screen. So: the largest rectangle of the picture's
 * shape that fits, centred.
 */
export function contentBox(boxW, boxH, picW, picH) {
  const w0 = Number(boxW) || 0;
  const h0 = Number(boxH) || 0;
  if (!(picW > 0) || !(picH > 0) || !(w0 > 0) || !(h0 > 0)) {
    return { x: 0, y: 0, w: w0, h: h0 };
  }
  const scale = Math.min(w0 / picW, h0 / picH);
  const w = picW * scale;
  const h = picH * scale;
  return { x: (w0 - w) / 2, y: (h0 - h) / 2, w, h };
}

/** The board's 16:9 paper inside whatever box it was given. */
export const letterbox = (boxW, boxH, aspect = BOARD_ASPECT) => contentBox(boxW, boxH, aspect, 1);

/** A pixel in the box to a fraction of the picture, clamped to it. */
export function normalise(px, py, box) {
  if (!box || !(box.w > 0) || !(box.h > 0)) return [0, 0];
  return [round4(clamp01((px - box.x) / box.w)), round4(clamp01((py - box.y) / box.h))];
}

/** And back: a fraction of the picture to a pixel in this box. */
export function denormalise(point, box) {
  return [box.x + point[0] * box.w, box.y + point[1] * box.h];
}

/** Is a pixel inside the picture (not on a bar)? */
export const inside = (px, py, box) => px >= box.x && px <= box.x + box.w
  && py >= box.y && py <= box.y + box.h;

/* --------------------------------------------------------------- smoothing */

/**
 * A freehand line as curves rather than corners.
 *
 * Each point becomes the control point of a quadratic curve that ends half
 * way to the next one, so the line passes smoothly through the midpoints
 * instead of kinking at every sample. Returned as plain commands so it can be
 * checked without a canvas; tracePath below plays them onto one.
 */
export function smoothPath(pts) {
  const n = pts.length;
  if (n === 0) return [];
  const out = [{ op: 'M', x: pts[0][0], y: pts[0][1] }];
  if (n === 1) return out;
  if (n === 2) { out.push({ op: 'L', x: pts[1][0], y: pts[1][1] }); return out; }
  for (let i = 1; i < n - 1; i += 1) {
    const mx = (pts[i][0] + pts[i + 1][0]) / 2;
    const my = (pts[i][1] + pts[i + 1][1]) / 2;
    out.push({ op: 'Q', cx: pts[i][0], cy: pts[i][1], x: mx, y: my });
  }
  out.push({ op: 'L', x: pts[n - 1][0], y: pts[n - 1][1] });
  return out;
}

export function tracePath(ctx, pts) {
  for (const c of smoothPath(pts)) {
    if (c.op === 'M') ctx.moveTo(c.x, c.y);
    else if (c.op === 'Q') ctx.quadraticCurveTo(c.cx, c.cy, c.x, c.y);
    else ctx.lineTo(c.x, c.y);
  }
}

/* ----------------------------------------------------------------- erasing */

/** Distance from p to the segment ab, all in the same units. */
export function distToSegment(p, a, b) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = dx * dx + dy * dy;
  let t = len > 0 ? ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len : 0;
  t = Math.max(0, Math.min(1, t));
  const x = a[0] + t * dx - p[0];
  const y = a[1] + t * dy - p[1];
  return Math.sqrt(x * x + y * y);
}

const ellipsePoints = (a, b, n = 48) => {
  const cx = (a[0] + b[0]) / 2;
  const cy = (a[1] + b[1]) / 2;
  const rx = Math.abs(b[0] - a[0]) / 2;
  const ry = Math.abs(b[1] - a[1]) / 2;
  const out = [];
  for (let i = 0; i <= n; i += 1) {
    const t = (i / n) * Math.PI * 2;
    out.push([cx + rx * Math.cos(t), cy + ry * Math.sin(t)]);
  }
  return out;
};

/** The outline a stroke is drawn along, as a polyline (normalised). */
export function outlineOf(stroke) {
  const p = stroke.points || [];
  if (p.length === 0) return [];
  if (FREEHAND.has(stroke.tool)) return p;
  const a = p[0];
  const b = p[p.length - 1];
  if (stroke.tool === 'rect') return [a, [b[0], a[1]], b, [a[0], b[1]], a];
  if (stroke.tool === 'ellipse') return ellipsePoints(a, b);
  return [a, b];
}

/**
 * Does the eraser, at `pt` with radius `r` (a fraction of the surface's
 * HEIGHT), touch this stroke?
 *
 * Measured with x stretched by the surface's aspect, so a circle is a circle:
 * in normalised units a tenth across a 16:9 board is nearly twice as far as
 * a tenth down it. A rectangle or an ellipse is erased by touching its line,
 * as on any whiteboard; a label by touching the text.
 */
export function hitStroke(stroke, pt, r, aspect = 1) {
  if (!stroke || !Array.isArray(stroke.points) || stroke.points.length === 0) return false;
  const s = (q) => [q[0] * aspect, q[1]];
  const p = s(pt);
  if (stroke.tool === 'text') {
    const [x, y] = s(stroke.points[0]);
    const size = ((stroke.width || WIDTHS.text) / 1000) * aspect;
    const w = Math.max(1, String(stroke.text || '').length) * size * 0.58;
    return p[0] >= x - r && p[0] <= x + w + r && p[1] >= y - r && p[1] <= y + size * 1.2 + r;
  }
  const half = (((stroke.width || 3) / 1000) * aspect) / 2;
  const line = outlineOf(stroke).map(s);
  if (line.length === 1) {
    const d = Math.hypot(line[0][0] - p[0], line[0][1] - p[1]);
    return d <= r + half;
  }
  for (let i = 1; i < line.length; i += 1) {
    if (distToSegment(p, line[i - 1], line[i]) <= r + half) return true;
  }
  return false;
}

/** Every stroke the eraser touches, newest first. */
export function eraserHits(strokes, pt, r, aspect = 1) {
  const out = [];
  for (let i = strokes.length - 1; i >= 0; i -= 1) {
    if (hitStroke(strokes[i], pt, r, aspect)) out.push(strokes[i].id);
  }
  return out;
}

/* ------------------------------------------------------------- validation */

const isPoint = (p) => Array.isArray(p) && p.length === 2
  && Number.isFinite(p[0]) && Number.isFinite(p[1])
  && p[0] >= -0.01 && p[0] <= 1.01 && p[1] >= -0.01 && p[1] <= 1.01;

const cleanPoints = (list, max) => {
  if (!Array.isArray(list) || list.length > max) return null;
  const out = [];
  for (const p of list) {
    if (!isPoint(p)) return null;
    out.push([round4(clamp01(p[0])), round4(clamp01(p[1]))]);
  }
  return out;
};

/** Plain text, one line, no control characters. Never markup: fillText only. */
const cleanText = (t) => Array.from(String(t).slice(0, MAX_TEXT * 2))
  .map((c) => { const n = c.charCodeAt(0); return n < 32 || n === 127 ? ' ' : c; })
  .join('').slice(0, MAX_TEXT);

/** A stroke as it may be kept, or null. */
function cleanStroke(m, maxPoints) {
  if (!m || typeof m !== 'object') return null;
  if (typeof m.id !== 'string' || !ID_RE.test(m.id)) return null;
  if (typeof m.target !== 'string' || !TARGET_RE.test(m.target)) return null;
  if (!TOOLS.includes(m.tool)) return null;
  if (!Object.prototype.hasOwnProperty.call(COLORS, m.color)) return null;
  const width = Number(m.width);
  if (!Number.isFinite(width) || width < 0.5 || width > 80) return null;
  const points = cleanPoints(m.points, maxPoints);
  if (!points) return null;
  const out = { id: m.id, target: m.target, tool: m.tool, color: m.color, width, points, done: m.done === true };
  if (m.tool === 'text') {
    if (typeof m.text !== 'string' || !m.text.trim()) return null;
    out.text = cleanText(m.text);
  }
  if (typeof m.author === 'string' && ID_RE.test(m.author)) out.author = m.author;
  return out;
}

/**
 * Read one message from a peer, or refuse it.
 *
 * Everything a peer sends is somebody else's input arriving on my page, so
 * it is measured before it is parsed, parsed with JSON.parse and nothing
 * else, and then rebuilt field by field: an unknown kind, a colour that is
 * not one of ours, a point off the surface, a string where a number goes -
 * any of them and the whole message is dropped. Nothing is ever evaluated,
 * and nothing is ever rendered as HTML.
 */
export function parseMessage(raw) {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > MAX_MESSAGE) return null;
  let m;
  try { m = JSON.parse(raw); } catch { return null; }
  return validateMessage(m);
}

export function validateMessage(m) {
  if (!m || typeof m !== 'object' || Array.isArray(m)) return null;
  switch (m.t) {
    case 'stroke': {
      const s = cleanStroke(m, MAX_BATCH);
      return s ? { t: 'stroke', ...s } : null;
    }
    case 'erase': {
      if (!Array.isArray(m.ids) || m.ids.length === 0 || m.ids.length > 500) return null;
      if (!m.ids.every((id) => typeof id === 'string' && ID_RE.test(id))) return null;
      return { t: 'erase', ids: [...m.ids] };
    }
    case 'clear': {
      if (typeof m.target !== 'string' || !TARGET_RE.test(m.target)) return null;
      if (m.scope !== 'mine' && m.scope !== 'all') return null;
      return { t: 'clear', target: m.target, scope: m.scope };
    }
    case 'laser': {
      if (typeof m.target !== 'string' || !TARGET_RE.test(m.target)) return null;
      if (!isPoint([m.x, m.y])) return null;
      return { t: 'laser', target: m.target, x: round4(clamp01(m.x)), y: round4(clamp01(m.y)) };
    }
    case 'sync-req':
      return { t: 'sync-req' };
    case 'board-open':
      return { t: 'board-open' };
    case 'sync': {
      if (typeof m.target !== 'string' || !TARGET_RE.test(m.target)) return null;
      if (!Array.isArray(m.strokes) || m.strokes.length > MAX_STROKES) return null;
      const strokes = [];
      for (const s of m.strokes) {
        const c = cleanStroke({ ...s, target: m.target }, MAX_POINTS);
        // One bad stroke in a sync is that stroke's problem, not the board's.
        if (c) strokes.push(c);
      }
      return { t: 'sync', target: m.target, strokes, open: m.open === true };
    }
    default:
      return null;
  }
}

/* ------------------------------------------------------------- the store */

/**
 * Everything drawn, by surface. A plain object of Maps, mutated in place by
 * the functions below - the canvases redraw from it on their own, so a
 * stroke growing thirty times a second never becomes a React render.
 */
export function createStore() {
  return { targets: new Map() };
}

const surface = (store, target) => {
  let s = store.targets.get(target);
  if (!s) { s = new Map(); store.targets.set(target, s); }
  return s;
};

/** The strokes on one surface, oldest first (the order they are drawn in). */
export const strokesOf = (store, target) => [...(store.targets.get(target)?.values() || [])];

/**
 * A stroke arriving, or growing.
 *
 * A freehand stroke's points are the NEW ones since the last message, and are
 * appended; a shape's are its two corners as they are now, and replace. A
 * stroke id already held by somebody else is left alone: an author owns their
 * own lines, and a peer cannot redraw mine by reusing my id.
 */
export function upsertStroke(store, s) {
  const map = surface(store, s.target);
  const cur = map.get(s.id);
  if (cur) {
    if (cur.author !== s.author) return false;
    if (FREEHAND.has(cur.tool)) {
      const room = MAX_POINTS - cur.points.length;
      if (room > 0) cur.points.push(...s.points.slice(0, room));
    } else {
      cur.points = s.points;
    }
    if (s.text !== undefined) cur.text = s.text;
    cur.done = cur.done || s.done;
    return true;
  }
  if (map.size >= MAX_STROKES) return false;
  map.set(s.id, { id: s.id, author: s.author, target: s.target, tool: s.tool,
    color: s.color, width: s.width, points: [...s.points], text: s.text, done: s.done });
  return true;
}

/** Remove strokes by id, wherever they are. Returns the surfaces touched. */
export function eraseIds(store, ids) {
  const touched = new Set();
  for (const [target, map] of store.targets) {
    for (const id of ids) if (map.delete(id)) touched.add(target);
  }
  return touched;
}

/** Clear a surface: everybody's strokes, or one author's. */
export function clearTarget(store, target, author = null) {
  const map = store.targets.get(target);
  if (!map) return false;
  if (!author) { const had = map.size > 0; map.clear(); return had; }
  let had = false;
  for (const [id, s] of map) if (s.author === author) { map.delete(id); had = true; }
  return had;
}

/**
 * What a late joiner is missing. Strokes they already have are kept - the
 * longer of the two, should one of them have caught a stroke mid-way.
 */
export function mergeSync(store, target, strokes) {
  const map = surface(store, target);
  let changed = false;
  for (const s of strokes) {
    const cur = map.get(s.id);
    if (cur && cur.points.length >= s.points.length) continue;
    if (!cur && map.size >= MAX_STROKES) break;
    map.set(s.id, { ...s, points: [...s.points] });
    changed = true;
  }
  return changed;
}

/**
 * A surface as sync messages, each under MAX_MESSAGE. Always at least one,
 * so the board's open/closed state reaches a late joiner even when nothing
 * has been drawn on it yet.
 */
export function syncMessages(store, target, open = false) {
  const all = strokesOf(store, target).map((s) => ({ id: s.id, author: s.author, tool: s.tool,
    color: s.color, width: s.width, points: s.points, text: s.text, done: true }));
  const out = [];
  let batch = [];
  let size = 200;
  for (const s of all) {
    const n = JSON.stringify(s).length + 1;
    if (n > MAX_MESSAGE - 300) continue;
    if (size + n > MAX_MESSAGE - 300 && batch.length) {
      out.push({ t: 'sync', target, strokes: batch, open });
      batch = [];
      size = 200;
    }
    batch.push(s);
    size += n;
  }
  out.push({ t: 'sync', target, strokes: batch, open });
  return out;
}

/* --------------------------------------------------------------- drawing */

/**
 * Paint strokes onto a 2D context, inside `box` (pixels).
 *
 * `colorOf` turns a colour name into a real colour - the page reads its own
 * tokens; a test hands in a table. Used by the live canvases, the PNG export,
 * the marked-up snapshot and the recording, so all four look the same.
 */
export function renderStrokes(ctx, strokes, box, colorOf) {
  const unit = box.w / 1000;
  for (const s of strokes) {
    const pts = (s.points || []).map((p) => denormalise(p, box));
    if (pts.length === 0) continue;
    const color = colorOf(s.color) || '#888';
    const lw = Math.max(s.tool === 'highlighter' ? MIN_PX.highlighter : MIN_PX.other, s.width * unit);
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = lw;
    if (s.tool === 'highlighter') {
      ctx.globalAlpha = 0.34;
      ctx.lineCap = 'butt';
    }
    if (s.tool === 'text') {
      const size = Math.max(10, s.width * unit);
      ctx.font = `600 ${size}px system-ui, -apple-system, "Segoe UI", sans-serif`;
      ctx.textBaseline = 'top';
      ctx.fillText(String(s.text || ''), pts[0][0], pts[0][1]);
    } else if (pts.length === 1 && FREEHAND.has(s.tool)) {
      ctx.beginPath();
      ctx.arc(pts[0][0], pts[0][1], lw / 2, 0, Math.PI * 2);
      ctx.fill();
    } else if (FREEHAND.has(s.tool)) {
      ctx.beginPath();
      tracePath(ctx, pts);
      ctx.stroke();
    } else {
      const a = pts[0];
      const b = pts[pts.length - 1];
      ctx.beginPath();
      if (s.tool === 'rect') {
        ctx.rect(Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.abs(b[0] - a[0]), Math.abs(b[1] - a[1]));
      } else if (s.tool === 'ellipse') {
        ctx.ellipse((a[0] + b[0]) / 2, (a[1] + b[1]) / 2,
          Math.abs(b[0] - a[0]) / 2, Math.abs(b[1] - a[1]) / 2, 0, 0, Math.PI * 2);
      } else {
        ctx.moveTo(a[0], a[1]);
        ctx.lineTo(b[0], b[1]);
        if (s.tool === 'arrow') {
          const ang = Math.atan2(b[1] - a[1], b[0] - a[0]);
          const head = Math.max(10, lw * 4);
          for (const d of [-0.45, 0.45]) {
            ctx.moveTo(b[0], b[1]);
            ctx.lineTo(b[0] - head * Math.cos(ang + d), b[1] - head * Math.sin(ang + d));
          }
        }
      }
      ctx.stroke();
    }
    ctx.restore();
  }
}

/** A fresh stroke id: mine, unique, and short. */
export const newId = (me, n) => `${String(me || 'x').replace(/[^\w-]/g, '').slice(0, 40)}-${n}-${Math.random().toString(36).slice(2, 8)}`;
